import React, { useEffect, useState, useCallback } from "react";
import {
  useDispatch,
  useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import { resolveImageUrl } from "../../../utils/image";
import {
  PlusCircleOutlined,
  ExclamationCircleOutlined,
  PictureOutlined,
  OrderedListOutlined,
  DragOutlined,
  } from "@ant-design/icons";
import {
  Button,
  Table,
  Form,
  message,
  Tooltip,
  Popconfirm,
  Switch,
  Select,
  Space,
  Typography,
  Badge,
  Card,
  Image,
  Input,
} from "antd";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { setCurrentPage, setPageSize } from "../../../redux/banner/banner";
import {
  getBanners,
  deleteBanner,
  toggleBannerStatus,
  updateBannerOrder,
} from "../../../api/banner/banner";
import BannerFormDrawer from "./BannerFormDrawer";
import PageHeader from "../../../components/PageLayout/PageHeader";
import FilterBar from "../../../components/PageLayout/FilterBar";
import ButtonWithPermission from "../../../components/ButtonWithPermission/ButtonWithPermission";
import RowActions from "../../../components/cms/RowActions";
import StatusTag from "../../../components/cms/StatusTag";
import { ACTION_ICON, confirmDelete } from "../../../components/cms/actions";
import { AUDIENCE_LABEL, TARGET_LABEL } from "./bannerConstants";

const { Text } = Typography;
const { Search } = Input;

// Hàng kéo-thả để sắp xếp thứ tự
const DraggableRow = ({ index, moveRow, className, style, ...restProps }) => {
  const ref = React.useRef();
  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: "DraggableRow",
    collect: (monitor) => {
      const { index: dragIndex } = monitor.getItem() || {};
      if (dragIndex === index) {
        return {};
      }
      return {
        isOver: monitor.isOver(),
        dropClassName:
          dragIndex < index ? "drop-over-downward" : "drop-over-upward",
      };
    },
    drop: (item) => {
      moveRow(item.index, index);
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: "DraggableRow",
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drop(drag(ref));

  return (
    <tr
      ref={ref}
      className={`${className} ${isOver ? dropClassName : ""}`}
      style={{ cursor: "move", opacity: isDragging ? 0.5 : 1, ...style }}
      {...restProps}
    />
  );
};

export default function BannerTable() {
  const { banners, isLoading, currentPage, pageSize, totalRecords } =
    useSelector((state) => state.persistedReducer.bannerSlice);

  const dispatch = useDispatch();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("add");
  const [recordEdit, setRecordEdit] = useState(null);
  const [form] = Form.useForm();
  const [statusFilter, setStatusFilter] = useState(null);
  const [titleSearch, setTitleSearch] = useState("");
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [isDraggable, setIsDraggable] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => {
    if (banners?.length) {
      setDataSource(banners);
    }
  }, [banners]);

  useEffect(() => {
    dispatch(
      getBanners({
        currentPage,
        pageSize,
        status: statusFilter,
        title: titleSearch,
      })
    );
    setFiltersApplied(statusFilter !== null || titleSearch !== "");
  }, [dispatch, currentPage, pageSize, statusFilter, titleSearch]);

  const openAddDrawer = () => {
    setDrawerMode("add");
    setRecordEdit(null);
    setDrawerOpen(true);
  };

  const openEditDrawer = (record) => {
    setDrawerMode("edit");
    setRecordEdit(record);
    setDrawerOpen(true);
  };

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const handlePageChange = (page) => dispatch(setCurrentPage(page));
  const handleSizeChange = useCallback(
    (_current, size) => dispatch(setPageSize(size)),
    [dispatch]
  );

  const refreshList = () => dispatch(getBanners({ currentPage, pageSize }));

  const deleteBannerRecord = async (id) => {
    try {
      await dispatch(deleteBanner(id));
      message.success("Xóa banner thành công");
      refreshList();
    } catch (err) {
      message.error("Không thể xóa banner");
    }
  };

  const handleStatusChange = async (record) => {
    setTogglingId(record.id);
    try {
      await dispatch(toggleBannerStatus(record.id));
      message.success("Cập nhật trạng thái thành công");
      refreshList();
    } catch (err) {
      message.error("Không thể cập nhật trạng thái");
    } finally {
      setTogglingId(null);
    }
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value ?? null);
    dispatch(setCurrentPage(1));
  };

  const handleTitleSearch = (value) => {
    setTitleSearch(value);
    dispatch(setCurrentPage(1));
  };

  const clearAllFilters = () => {
    setStatusFilter(null);
    setTitleSearch("");
    dispatch(setCurrentPage(1));
  };

  const moveRow = useCallback(
    (dragIndex, hoverIndex) => {
      if (!isDraggable) return;
      const dragRow = dataSource[dragIndex];
      const newData = [...dataSource];
      newData.splice(dragIndex, 1);
      newData.splice(hoverIndex, 0, dragRow);
      setDataSource(newData);
    },
    [dataSource, isDraggable]
  );

  const saveOrder = async () => {
    try {
      const orders = {};
      dataSource.forEach((item, index) => {
        orders[item.id] = index + 1;
      });
      await dispatch(updateBannerOrder({ data: { orders } }));
      message.success("Cập nhật thứ tự thành công");
      setIsDraggable(false);
      refreshList();
    } catch (err) {
      message.error("Không thể cập nhật thứ tự");
    }
  };

  const cancelOrderEdit = () => {
    setIsDraggable(false);
    setDataSource([...banners]);
  };

  const columns = [
    {
      title: "#",
      dataIndex: "order",
      key: "index",
      width: 70,
      fixed: "left",
      render: (_text, _record, index) =>
        isDraggable ? <DragOutlined /> : (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "Hình ảnh",
      dataIndex: "image_path",
      key: "image_path",
      fixed: "left",
      width: 200,
      render: (image_path) => (
        <Image
          src={resolveImageUrl(image_path)}
          alt="Banner"
          width={120}
          height={60}
          style={{ objectFit: "cover", borderRadius: 6 }}
          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAA"
        />
      ),
    },
    {
      title: "Tiêu đề",
      dataIndex: "title",
      key: "title",
      width: 240,
      render: (title) => <Text strong>{title}</Text>,
    },
    {
      // Đối tượng là LOẠI, không phải trạng thái -> tag xám (xem StatusTag).
      title: "Hiển thị cho",
      dataIndex: "audience",
      key: "audience",
      width: 150,
      render: (audience) => <StatusTag>{AUDIENCE_LABEL[audience] || "Tất cả"}</StatusTag>,
    },
    {
      title: "Khi bấm",
      dataIndex: "link_url",
      key: "link_url",
      width: 170,
      render: (link) =>
        link ? (
          <Text>{TARGET_LABEL[link] || link}</Text>
        ) : (
          <Text type="secondary">Không điều hướng</Text>
        ),
    },
    {
      title: "Thứ tự",
      dataIndex: "order",
      key: "order",
      align: "center",
      width: 120,
      render: (order) => <b>{order}</b>,
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      align: "center",
      width: 140,
      render: (status) => (
        <Badge
          status={status === true ? "success" : "error"}
          text={status === true ? "Hiển thị" : "Ẩn"}
        />
      ),
    },
    {
      title: "Thao tác",
      dataIndex: "operation",
      align: "center",
      width: 130,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Popconfirm
            title="Thay đổi trạng thái"
            description={`Bạn có chắc chắn muốn ${
              record.status === true ? "ẩn" : "hiển thị"
            } banner này?`}
            onConfirm={() => handleStatusChange(record)}
            okText="Cập nhật"
            cancelText="Hủy"
            icon={<ExclamationCircleOutlined style={{ color: "red" }} />}
          >
            <Tooltip title="Cập nhật trạng thái">
              <Switch
                size="small"
                checked={record.status === true}
                loading={togglingId === record.id}
              />
            </Tooltip>
          </Popconfirm>
          <RowActions
            items={[
              {
                key: "edit",
                icon: ACTION_ICON.edit,
                label: "Chỉnh sửa",
                permission: ["banners.update"],
                onClick: () => openEditDrawer(record),
              },
              {
                key: "delete",
                icon: ACTION_ICON.delete,
                label: "Xóa banner",
                danger: true,
                divider: true,
                permission: ["banners.destroy"],
                onClick: () =>
                  confirmDelete(record.title || `banner #${record.id}`, () =>
                    deleteBannerRecord(record.id)
                  ),
              },
            ]}
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Quản lý banner"
        subtitle="Quản lý hình ảnh / slide hiển thị trên mini app"
        breadcrumb={[
          { title: "Quản lý giao diện" },
          { title: <NavLink to="/banners">Quản lý banner</NavLink> },
        ]}
        extra={
          <ButtonWithPermission
            actions={["banners.store"]}
            children={"Thêm mới"}
            type="primary"
            icon={<PlusCircleOutlined />}
            onClick={(e) => {
              e?.preventDefault();
              openAddDrawer();
            }}
            noPermissionText={"Không có quyền truy cập"}
          />
        }
      />

      <FilterBar>
        <Search
          placeholder="Tìm kiếm theo tiêu đề"
          value={titleSearch}
          onChange={(e) => setTitleSearch(e.target.value)}
          onSearch={handleTitleSearch}
          allowClear
          enterButton
          style={{ width: 320, maxWidth: "100%" }}
        />
        <Select
          allowClear
          style={{ width: 180 }}
          placeholder="Trạng thái"
          value={statusFilter}
          onChange={handleStatusFilter}
          options={[
            { value: "1", label: "Hiển thị" },
            { value: "0", label: "Ẩn" },
          ]}
        />
        {filtersApplied && <Button onClick={clearAllFilters}>Xóa bộ lọc</Button>}
      </FilterBar>

      <Card
        style={{ margin: 16 }}
        title={
          <Space>
            <PictureOutlined />
            <span>Danh sách banner</span>
          </Space>
        }
        extra={
          isDraggable ? (
            <Space>
              <Button
                onClick={saveOrder}
                type="primary"
                icon={<OrderedListOutlined />}
              >
                Lưu thứ tự
              </Button>
              <Button onClick={cancelOrderEdit}>Hủy</Button>
            </Space>
          ) : (
            <Button
              onClick={() => setIsDraggable(true)}
              icon={<OrderedListOutlined />}
            >
              Sắp xếp thứ tự
            </Button>
          )
        }
      >
        <DndProvider backend={HTML5Backend}>
          <Form form={form} component={false}>
            <Table
              columns={columns}
              loading={isLoading}
              dataSource={isDraggable ? dataSource : banners}
              rowKey="id"
              pagination={
                isDraggable
                  ? false
                  : {
                      showTotal: (total, range) =>
                        `Hiển thị từ ${range[0]} -> ${range[1]} trong tổng ${total} giá trị`,
                      showQuickJumper: true,
                      showSizeChanger: true,
                      onShowSizeChange: handleSizeChange,
                      total: totalRecords,
                      current: currentPage,
                      onChange: handlePageChange,
                      pageSizeOptions: ["10", "20", "50"],
                    }
              }
              components={
                isDraggable ? { body: { row: DraggableRow } } : {}
              }
              onRow={(_, index) => ({ index, moveRow })}
              className={isDraggable ? "draggable-table" : ""}
              size="small"
              scroll={{ x: "max-content" }}
            />
          </Form>
        </DndProvider>
      </Card>

      <BannerFormDrawer
        open={drawerOpen}
        mode={drawerMode}
        record={recordEdit}
        currentPage={currentPage}
        pageSize={pageSize}
        onClose={closeDrawer}
      />

      {/* CSS cho hàng kéo-thả */}
      <style jsx>{`
        .draggable-table tr.drop-over-downward td {
          border-bottom: 2px dashed #1890ff;
        }
        .draggable-table tr.drop-over-upward td {
          border-top: 2px dashed #1890ff;
        }
      `}</style>
    </div>
  );
}
